import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ErrorBoundary from './components/ErrorBoundary';

// Filter out browser extension errors from console in development
if (process.env.NODE_ENV === 'development') {
  // Helper function to check if error is from browser extension
  const isExtensionError = (message, source) => {
    if (!message) {
      // Check source if message is empty
      if (source) {
        const src = source.toString().toLowerCase();
        return (
          src.includes('chrome-extension://') ||
          src.includes('moz-extension://') ||
          src.includes('safari-extension://') ||
          src.includes('contentScript.bundle.js') ||
          src.includes('content-script.js') ||
          src.includes('sentry.io') ||
          src.includes('ingest.sentry.io')
        );
      }
      return false;
    }
    const msg = message.toString().toLowerCase();
    const src = source?.toString().toLowerCase() || '';
    const combined = msg + ' ' + src;
    
    // Check for Sentry URLs in any form
    const hasSentryUrl = combined.includes('sentry.io') || combined.includes('ingest.sentry.io') || 
                         src.includes('sentry.io') || src.includes('ingest.sentry.io') ||
                         msg.includes('sentry.io') || msg.includes('ingest.sentry.io');
    
    // Check for 429 errors
    const has429 = combined.includes('429') || msg.includes('429') || src.includes('429');
    
    return (
      msg.includes('chrome-extension://') ||
      msg.includes('moz-extension://') ||
      msg.includes('safari-extension://') ||
      msg.includes('contentScript.bundle.js') ||
      msg.includes('content-script.js') ||
      msg.includes('google-docs-fte.js') ||
      msg.includes('showonechild.js') ||
      msg.includes('fte-utils.js') ||
      msg.includes('sentry.io') ||
      msg.includes('ingest.sentry.io') ||
      msg.includes('userreportlinkedcandidate') ||
      msg.includes('linkedcandidate') ||
      msg.includes('no resume url') ||
      msg.includes('err_file_not_found') ||
      msg.includes('failed to load resource') ||
      msg.includes('failed to fetch dynamically imported module') ||
      msg.includes('too many requests') ||
      (has429 && hasSentryUrl) ||
      (has429 && (msg.includes('rate') || msg.includes('ingest') || msg.includes('too many'))) ||
      (combined.includes('post') && hasSentryUrl && has429) ||
      src.includes('chrome-extension://') ||
      src.includes('moz-extension://') ||
      src.includes('safari-extension://') ||
      src.includes('content-script.js') ||
      src.includes('sentry.io') ||
      src.includes('ingest.sentry.io') ||
      (hasSentryUrl && (has429 || msg.includes('failed to load')))
    );
  };


  // Filter console.warn - only suppress console output
  const originalWarn = console.warn;
  console.warn = (...args) => {
    // Check all arguments for extension errors
    const allArgs = args.map(arg => arg?.toString() || '').join(' ');
    if (isExtensionError(allArgs, '')) {
      return; // Suppress extension warnings from console
    }
    originalWarn.apply(console, args);
  };

  // Filter unhandled promise rejections from extensions - set up early
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    let errorMessage = '';
    let errorStack = '';
    
    if (reason) {
      if (typeof reason === 'string') {
        errorMessage = reason;
      } else if (reason instanceof Error) {
        errorMessage = reason.message || reason.toString() || '';
        errorStack = reason.stack || '';
      } else {
        errorMessage = reason.toString() || '';
      }
    }
    
    // Also check the error source from stack trace
    const fullError = errorMessage + ' ' + errorStack;
    if (isExtensionError(errorMessage, errorStack) || isExtensionError(fullError, '')) {
      // Suppress the error from showing in console
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);
  
  // Also catch errors that might be logged before our handlers are set up
  // Filter console.error for promise rejections
  const originalError = console.error;
  console.error = (...args) => {
    // Check all arguments together for extension errors
    const allArgs = args.map(arg => arg?.toString() || '').join(' ');
    if (isExtensionError(allArgs, '')) {
      return; // Suppress extension errors from console
    }
    originalError.apply(console, args);
  };

  // Filter network resource loading errors
  window.addEventListener('error', (event) => {
    const errorSource = event.filename || event.target?.src || event.target?.href || '';
    const errorMessage = event.message || '';
    const combined = errorMessage + ' ' + errorSource;
    
    if (isExtensionError(combined, errorSource) || isExtensionError(errorMessage, errorSource)) {
      // Suppress extension network errors
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  }, true);
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
